import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { api } from "../api/client";
import { qk } from "../api/queries";
import { Icon } from "../components/Icon";
import { useMe } from "../lib/auth";

export function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const next = params.get("next");
  const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
  // An MCP client's sign-in (server-rendered page, not part of the app): a full page load.
  const serverNext = next && next.startsWith("/oauth/authorize?") ? next : null;

  const login = useMutation({
    mutationFn: () => api.login(username, password),
    meta: { inlineError: true },
    onSuccess: (user) => {
      qc.clear();
      qc.setQueryData(qk.me, user);
      if (serverNext) window.location.assign(serverNext);
      else navigate(safeNext, { replace: true });
    },
  });

  if (me && serverNext) {
    window.location.assign(serverNext);
    return null;
  }
  if (me) return <Navigate to={safeNext} replace />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          <span className="brand-mark" aria-hidden="true">
            <Icon name="board" size={20} />
          </span>
          <h1>Agent Task Runner</h1>
        </div>
        <p className="muted">Sign in to continue</p>
        <label className="field">
          <span className="field-label">Username</span>
          <input
            className="input"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </label>
        <label className="field">
          <span className="field-label">Password</span>
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {login.error && <div className="error-box">{login.error.message}</div>}
        <button className="btn btn-primary btn-block" disabled={login.isPending}>
          {login.isPending ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
